import { Logger } from '@feature-hub/core';
import { ContentImage, ContentImageAsset } from '../EditorContentTypes';
import { ScreenOrientation } from '../FeatureAppTypes';

export interface ContentAudiImage {
  image: [
    {
      imageFormats: {
        'web-largest': string;
      };
    },
  ];
}

export class AssetHelper {
  static getAssetDataObject(jsonObj: string): ContentAudiImage | string {
    if (typeof jsonObj === 'string' && jsonObj.startsWith('{')) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(jsonObj);
    }
    return jsonObj;
  }

  static replaceHost(url: string): string {
    const allowedHosts: string[] = ['audivmstest-a.akamaihd.net', 'audivms-a.akamaihd.net'];
    if (allowedHosts.includes(url)) {
      // pre-live host
      return url.replace(
        'audivmstest-a.akamaihd.net/brand/stage/brandportal/',
        'pre-www.audi.de/content-audi-assets/',
      );
    }
    if (allowedHosts.includes(url)) {
      // live host
      return url.replace(
        'audivms-a.akamaihd.net/brand/stage/brandportal/',
        'www.audi.de/content-audi-assets/',
      );
    }
    return url;
  }

  static createNumberStringWithFixedLength(number: number, length: number): string {
    let numberString = `${number}`;
    while (numberString.length < length) {
      numberString = `0${numberString}`;
    }
    return numberString;
  }

  static incrementImageUrl(originalImageUrl: string, index: number, logger?: Logger): string {
    const regex = /^(.*\/[^/]+[^0-9])([0-9]+)(\..+)$/;
    if (!regex.test(originalImageUrl)) {
      logger?.error(
        `Error incrementing number, pattern not recognised "${originalImageUrl}". They need to have a progressive number index => 0001 to 0050, for example`,
      );
    }

    if (originalImageUrl) {
      const regexMatch = originalImageUrl.match(regex);
      if (regexMatch && regexMatch.length === 4) {
        const number: number = parseInt(regexMatch[2], 10);
        const newNumber = AssetHelper.createNumberStringWithFixedLength(
          number + index,
          regexMatch[2].length,
        );

        return regexMatch[1] + newNumber + regexMatch[3];
      }
    }
    return '';
  }

  static generateImageUrls(
    media: ContentImage[],
    orientation: ScreenOrientation,
    numberOfImages: number,
    useEveryNthImage: number | undefined,
    logger?: Logger,
  ): string[] {
    if (media.length === 1 && numberOfImages) {
      // generate image URLs automatically based on the given number of images
      // check whether we want to use only every n-th image
      let step = 1; // by default use every image
      if (useEveryNthImage && useEveryNthImage > 1) {
        step = useEveryNthImage;
      }
      const template = AssetHelper.getImageUrl(media[0][orientation], logger);
      const images: string[] = [];
      for (let i = 0; i < numberOfImages; i += step) {
        images.push(AssetHelper.incrementImageUrl(template, i, logger));
      }

      return images;
    }
    return media.map((image) => AssetHelper.getImageUrl(image[orientation], logger));
  }

  static getImageUrl({ assetValue = '' }: ContentImageAsset, logger: Logger | undefined): string {
    // JSON from content.audi => extract image URL
    try {
      // check for normal URL
      if (typeof assetValue === 'string' && !assetValue.startsWith('{')) {
        // standard URL
        return assetValue;
      }
      // we have a json object
      const assetData = AssetHelper.getAssetDataObject(assetValue) as ContentAudiImage;

      // check if it is an image or video
      if (Object.prototype.hasOwnProperty.call(assetData, 'image')) {
        const { imageFormats } = assetData.image[0];
        const { 'web-largest': webLargest } = imageFormats;
        if (!webLargest) {
          throw new Error('web-largest not found in imageFormats');
        }
        return AssetHelper.replaceHost(webLargest);
      }
      // no video data
      logger?.error(`Error retrieving image or video URL from JSON.`, assetValue);
      return ''; // no image or video URL
    } catch (error) {
      logger?.error(`Error getting imageFormats["web-largest"] from image[0].`, assetValue, error);
      return ''; // no image in case of an error
    }
  }
}
